import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { ModalService } from '@shared/components/modal';

import { DialogFichierSapComponent } from './dialog-fichier-sap/dialog-fichier-sap.component';

import type { CanActivateFn } from '@angular/router';
import type { DialogRef } from '@angular/cdk/dialog';

/**
 * Ouvre l'export SAP sans quitter l'ecran courant.
 *
 * L'entree de menu reste une route — c'est la seule forme qu'accepte le modele
 * de navigation du kit — mais il n'y a plus de page a afficher : un selecteur de
 * dates et un bouton tiennent dans une modale. Le garde ouvre celle-ci puis
 * ANNULE la navigation, ce qui laisse la page en cours montee derriere.
 *
 * A placer en DERNIER dans `canActivate` : les gardes s'executent en sequence et
 * s'arretent au premier refus, donc la modale ne s'ouvre qu'une fois
 * l'authentification et les droits verifies.
 */

/**
 * Modale en cours, ou `null`.
 *
 * Un garde n'est pas une fonction pure ici : il a un effet de bord. Sans cette
 * memoire, deux navigations vers `/redditions/fichier-sap` — deux clics sur
 * l'entree de menu, un aller-retour d'historique — reexecutent le garde et
 * EMPILENT deux modales identiques. Verifie par test.
 */
let modaleOuverte: DialogRef<boolean> | null = null;

export const ouvrirFichierSapGuard: CanActivateFn = (): boolean | UrlTree => {
  const router = inject(Router);

  // Deja ouverte : ne pas en empiler une seconde, mais annuler la navigation
  // comme d'habitude pour laisser l'ecran courant en place.
  if (modaleOuverte === null) {
    const ref = inject(ModalService).openForm<boolean, undefined>(DialogFichierSapComponent);
    modaleOuverte = ref;
    ref.closed.subscribe(() => {
      modaleOuverte = null;
    });
  }

  // URL ouverte directement : il n'y a aucune page a conserver derriere la
  // modale, on affiche les redditions plutot qu'une coquille vide.
  return router.navigated ? false : router.parseUrl('/redditions/evenements');
};
